import { useFormikContext } from 'formik';

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import type { RecurringValues } from '@/schemas/batch-call.schema';

import { DAY_FULL, RECURRENCE_TYPE, WEEK_DAYS } from '@/constants';

function buildRecurringSummary(frequency: string, daysOfWeek: (string | undefined)[]): string {
  const freqLabel =
    RECURRENCE_TYPE.find(r => r.value === frequency)?.value.toLocaleLowerCase() ?? frequency;

  if (frequency === 'week') {
    const dayNames = daysOfWeek
      .filter(Boolean)
      .map(d => DAY_FULL[d!] ?? d)
      .join(', ');
    return dayNames ? `Every week on ${dayNames}` : 'Every week';
  }

  return `Every ${freqLabel}`;
}

export const RecurringCallField = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext<RecurringValues>();

  const isRecurring = values.isRecurring ?? false;
  const frequency = values.recurringFrequency ?? 'day';
  const daysOfWeek = values.recurringDaysOfWeek ?? [];

  const toggleDay = (val: string) => {
    const updated = daysOfWeek.includes(val)
      ? daysOfWeek.filter(d => d !== val)
      : [...daysOfWeek, val];
    setFieldValue('recurringDaysOfWeek', updated);
  };

  const handleToggleRecurring = (checked: boolean) => {
    setFieldValue('isRecurring', checked);
    if (checked) {
      setFieldValue('recurringFrequency', 'day');
      setFieldValue('recurringDaysOfWeek', []);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-zinc-950">Recurring</Label>
        <Switch checked={isRecurring} onCheckedChange={handleToggleRecurring} />
      </div>
      {isRecurring && (
        <div className="space-y-2.5">
          {/* Human-readable summary of current selection */}
          <p className="text-xs text-primary font-medium bg-primary/8 rounded-md px-2.5 py-1.5">
            {buildRecurringSummary(frequency, daysOfWeek)}
          </p>

          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 w-14 shrink-0">Every</span>
            <Select
              value={frequency}
              onValueChange={val => {
                setFieldValue('recurringFrequency', val);
                setFieldValue('recurringDaysOfWeek', []);
              }}
            >
              <SelectTrigger className="h-8 text-xs border-zinc-200 flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE_TYPE.map(type => (
                  <SelectItem value={type.value} key={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {frequency === 'week' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500 w-14 shrink-0">On</span>
              <div className="flex gap-1">
                {WEEK_DAYS.map(d => {
                  const { label, value } = d;
                  const active = daysOfWeek.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleDay(value)}
                      className={`w-7 h-7 rounded-full text-xs font-medium transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {touched.recurringDaysOfWeek && errors.recurringDaysOfWeek && (
                <p className="text-xs text-destructive">{errors.recurringDaysOfWeek as string}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
